import ApiService from '@/services/ApiService';
import AppEvent from '@/models/AppEvent';

export default {
    printConfig: null,

    /**
     * Initialize Electron Printing Service
     */
    async init(printConfig) {
        this.printConfig = printConfig

        if(isAllegra) {
            return printConfig;
        }

        // if main print server was never set before, set this machine to take that role
        // otherwise, get local config (machineIp and machineId) and check for inconsistencies
        // by comparing it with the one we got from server
        if (!printConfig.remote.printServerId) {
            printConfig = await this.setAsMainPrintServer(printConfig);
        } else {
            printConfig = await this.getLocalMachineConfig();
        }

        return printConfig
    },

    /**
     * Set the Machine as the main print server, update main
     * print server's info on the app server
     */
    async setAsMainPrintServer() {
        const result = window.electron.sendSync('el-print-server-set-main');

        if (!result || !result.success) return;

        this.printConfig.local.machineIp = result.ipAddress;
        this.printConfig.local.machineId = result.machineId;

        const response = await ApiService.patch('/print/config/server', {
            ipAddress: result.ipAddress,
            machineId: result.machineId
        })

        this.printConfig.local.isPrintServer = true;
        this.printConfig.local.printLocally = false;
        this.printConfig.remote.printServerIp = response.ipAddress;
        this.printConfig.remote.printServerId = response.machineId;

        AppEvent.build('printServerUpdate', {
            ipAddress: result.ipAddress,
            machineId: result.machineId
        }).send()

        return this.printConfig
    },

    /**
     * Get local machine config, and check if this machine is the main print server
     */
    async getLocalMachineConfig() {
        // if print server still not set up ignore this
        if (!this.printConfig.remote.printServerId || !this.printConfig.local.isPrintServer) {
            return this.printConfig;
        }

        let response = window.electron.sendSync('el-print-server-get-config');

        if(!response) {
            return this.printConfig;
        }

        this.printConfig.local.machineIp = response.ipAddress;
        this.printConfig.local.machineId = response.machineId;

        // if this machine is the main server but IP addresses do not match, save the new IP address
        if (this.printConfig.local.machineId === this.printConfig.remote.printServerId) {

            if (this.printConfig.local.machineIp !== this.printConfig.remote.printServerIp) {
                ApiService.patch('/print/config/server', {
                    ipAddress: this.printConfig.local.machineIp,
                    machineId: this.printConfig.local.machineId
                })

                AppEvent.build('printServerUpdate', {
                    ipAddress: this.printConfig.local.machineIp,
                    machineId: this.printConfig.local.machineId
                }).send()
            }

            this.printConfig.local.isPrintServer = true;
            this.printConfig.local.printLocally = false;
            this.printConfig.remote.printServerIp = this.printConfig.local.machineIp;
            this.printConfig.remote.printServerId = this.printConfig.local.machineId;
        }

        // if this machine was main print server before but is not anymore, update config
        else {
            this.printConfig.local.isPrintServer = false;
            this.printConfig.local.printLocally = true;
        }

        return this.printConfig
    },

    /**
     * Detect Printers
     */
    async detectPrinters() {
        const printers = window.electron.sendSync('el-print-server-detect');
        return printers.map(printer => {
            const defaultOptions = printer.options.escpos ? {
                width: 100,
                height: null,
                margin: null,
                charsPerLine: 48,
                dpi: 203,
                ipAddress: null,
                qrCodeSize: 200,
                other: {},
                version: 1
            } : {
                width: 58,
                height: 297,
                margin: 2,
                charsPerLine: 48,
                dpi: 203,
                ipAddress: null,
                qrCodeSize: null,
                other: {},
                version: 1
            }

            printer.options = { ...defaultOptions, ...printer.options }
            return printer
        })
    },

    /**
     * Send to printer
     *
     * @param printer
     * @param html
     * @param text
     * @param qrCode
     * @param options
     */
    async sendToPrinter(printer, html, text, qrCode, options = {}) {
        try {
            const result = window.electron.sendSync('el-print-server-print', printer, {
                html: html,
                text: text,
                qrCode: qrCode,
                qrCodeEmbed: options.qrCodeEmbed,
                openDrawer: !!options.openDrawer,
                highlight: options.highlight,
                footer: options.footer,
                codepage: options.codepage
            });

            if(result && result.success){
                return { success: true }
            } else {
                return { success: false, error: result ? result.error : 'no_electron_response' }
            }
        } catch(error) {
            console.log(error.message)
            return { success: false }
        }
    },

    /**
     * Open printer cash drawer
     */
    openDrawer(printer) {
        window.electron.send('el-open-drawer', printer)
    },
};
